import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CodeSimple, Broadcast } from "@phosphor-icons/react";

export default function Integrations() {
  const [affiliates, setAffiliates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/organizations?type=affiliate").then((r) => setAffiliates(r.data.organizations));
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns));
  }, []);

  // Only active campaigns get hosted-form links.
  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => c.is_active),
    [campaigns],
  );

  const backend = process.env.REACT_APP_BACKEND_URL;

  const example = (apiKey, campaignPublicId) => `curl -X POST ${backend}/api/leads/webhook \\
  -H "Content-Type: application/json" \\
  -d '{
    "affiliate_api_key": "${apiKey || "YOUR_API_KEY"}",
    "campaign_id": "PASTE_INTERNAL_CAMPAIGN_ID",
    "external_lead_id": "abc-123",
    "first_name": "Jane",
    "last_name": "Doe",
    "phone": "415-555-0100",
    "email": "jane@example.com",
    "state": "CA",
    "zip": "94103",
    "accident_date": "2026-01-15",
    "trustedform_url": "https://cert.trustedform.com/xyz",
    "sub_1": "google-cpc"
  }'`;

  return (
    <div>
      <div className="label-mono">Integrations</div>
      <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Endpoints</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="brut-card p-5">
          <div className="font-chivo font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
            <Broadcast size={16} /> Affiliate ingest webhook
          </div>
          <div className="text-xs font-mono mb-3">POST <b>{backend}/api/leads/webhook</b></div>
          {affiliates.map((a) => (
            <details key={a.id} className="border border-black mb-2">
              <summary className="cursor-pointer px-3 py-2 hover:bg-zinc-100">
                <span className="font-semibold">{a.name}</span> <span className="text-zinc-500 text-xs">· {a.public_source_id}</span>
              </summary>
              <div className="p-3 space-y-2">
                <div className="text-[11px] font-mono">
                  api_key: <span className="bg-zinc-100 px-1 border border-zinc-300">{a.api_key || "hidden"}</span>
                  {a.api_key && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(a.api_key); toast.success("Copied"); }}
                      className="ml-2 border border-black px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">Copy</button>
                  )}
                </div>
                <pre className="bg-zinc-950 text-zinc-100 p-3 text-[11px] overflow-x-auto whitespace-pre">{example(a.api_key)}</pre>
              </div>
            </details>
          ))}
          {!affiliates.length && <div className="label-mono">No affiliates yet.</div>}
        </div>

        <div className="brut-card p-5">
          <div className="font-chivo font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
            <CodeSimple size={16} /> Hosted web form
          </div>
          <div className="text-xs">Each active campaign has a hosted intake page:</div>
          <ul className="mt-3 text-sm font-mono space-y-2">
            {activeCampaigns.map((c) => (
              <li key={c.id} className="border-b border-zinc-200 pb-1">
                <a href={`/form/${c.public_id}`} target="_blank" rel="noreferrer" className="hover:underline">
                  {c.name}
                </a>
                <div className="text-[11px] text-zinc-500">/form/{c.public_id}</div>
              </li>
            ))}
            {!activeCampaigns.length && <li className="label-mono">No campaigns.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
